import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash2, Search, Download, Upload, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { formatMoney, docCurrency, currencySymbol } from "@/lib/currency";

const empty = {
  code: "", name: "", gstin: "",
  contact: "", email: "", phone: "",
  address: "", credit_limit: 0,
  billing_line1: "", billing_line2: "", billing_city: "", billing_state: "", billing_country: "India", billing_pin: "",
  same_as_billing: true,
  shipping_line1: "", shipping_line2: "", shipping_city: "", shipping_state: "", shipping_country: "India", shipping_pin: "",
};

const EMAIL_ERROR = "Please enter a valid email address.";
// Optional field — blank is fine; a non-blank value must look like a real
// email (rejects a mobile number or other non-email text typed in by mistake).
const isValidEmail = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export default function CustomersPage() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [emailError, setEmailError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => setList((await api.get("/customers")).data);
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setEmailError(""); setOpen(true); };
  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ ...empty, ...c });
    setEmailError("");
    setOpen(true);
  };

  const onEmailChange = (v) => {
    setForm((f) => ({ ...f, email: v }));
    setEmailError(isValidEmail(v) ? "" : EMAIL_ERROR);
  };

  const save = async () => {
    if (busy) return;
    if (!form.code || !form.name) return toast.error("Code and Name are required");
    if (!isValidEmail(form.email)) {
      setEmailError(EMAIL_ERROR);
      return toast.error(EMAIL_ERROR);
    }
    // mirror legacy single-line address so other pages keep working
    const billing = [form.billing_line1, form.billing_line2, form.billing_city, form.billing_state, form.billing_pin, form.billing_country]
      .filter(Boolean).join(", ");
    const payload = { ...form, address: form.address || billing };
    setBusy(true);
    try {
      if (editing) await api.put(`/customers/${editing}`, payload);
      else await api.post("/customers", payload);
      toast.success(editing ? "Customer updated" : "Customer created");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.code}?`)) return;
    try {
      await api.delete(`/customers/${c.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadCsv = async (path, filename) => {
    try {
      const { data } = await api.get(path, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/customers/import-csv", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const msg = `Imported ${file.name} · created ${data.created}, updated ${data.updated}` +
        (data.errors?.length ? ` · ${data.errors.length} row error(s)` : "");
      if (data.errors?.length) toast.warning(msg, { description: data.errors.slice(0, 3).join(" | ") });
      else toast.success(msg);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
  };

  const filtered = list.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.code || "").toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q)
      || (c.gstin || "").toLowerCase().includes(q) || (c.contact || "").toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Sales</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Customer Master
          </h1>
          <p className="text-sm text-slate-500 mt-1">GSTIN, billing & shipping addresses, contact, credit limit. Bulk import/export supported.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => downloadCsv("/customers/template-csv", "customers_template.csv")} data-testid="cust-csv-template-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Template
          </Button>
          <Button variant="outline" onClick={() => downloadCsv("/customers/export-csv", "customers_export.csv")} data-testid="cust-csv-export-btn">
            <Download className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <label className="inline-flex items-center h-10 rounded-md border border-slate-200 px-3 text-sm cursor-pointer hover:bg-slate-50">
            <Upload className="w-4 h-4 mr-1.5" /> Import CSV
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={onImport} data-testid="cust-csv-import-input" />
          </label>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={startCreate} data-testid="new-customer-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New Customer
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-3xl overflow-y-auto">
            <SheetHeader><SheetTitle data-testid="cust-sheet-title">{editing ? "Edit Customer" : "New Customer"}</SheetTitle></SheetHeader>

            <div className="space-y-6 mt-6">
              {/* Identity */}
              <section>
                <div className="text-sm font-semibold text-slate-700 mb-2">Identity</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Customer Code *</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="cust-code-input" /></div>
                  <div><Label>Customer Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cust-name-input" /></div>
                  <div><Label>GST Number</Label><Input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value })} placeholder="29ABCDE1234F1Z5" data-testid="cust-gstin-input" /></div>
                  <div><Label>Credit Limit ({currencySymbol(docCurrency(form.commercial))})</Label><Input type="number" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value || 0) })} /></div>
                </div>
              </section>

              {/* Contact */}
              <section>
                <div className="text-sm font-semibold text-slate-700 mb-2">Primary Contact</div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>Contact Person</Label><Input value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="cust-contact-input" /></div>
                  <div><Label>Mobile Number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="cust-phone-input" /></div>
                  <div>
                    <Label>Email ID</Label>
                    <Input
                      type="email"
                      value={form.email}
                      onChange={(e) => onEmailChange(e.target.value)}
                      className={emailError ? "border-red-400 focus-visible:ring-red-400" : ""}
                      data-testid="cust-email-input"
                    />
                    {emailError && <div className="text-xs text-red-600 mt-1" data-testid="cust-email-error">{emailError}</div>}
                  </div>
                </div>
              </section>

              {/* Billing Address */}
              <section>
                <div className="text-sm font-semibold text-slate-700 mb-2">Billing Address</div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.billing_line1} onChange={(e) => setForm({ ...form, billing_line1: e.target.value })} data-testid="cust-bill-line1" /></div>
                  <div className="col-span-2"><Label>Address Line 2</Label><Input value={form.billing_line2} onChange={(e) => setForm({ ...form, billing_line2: e.target.value })} /></div>
                  <div><Label>City</Label><Input value={form.billing_city} onChange={(e) => setForm({ ...form, billing_city: e.target.value })} data-testid="cust-bill-city" /></div>
                  <div><Label>State</Label><Input value={form.billing_state} onChange={(e) => setForm({ ...form, billing_state: e.target.value })} /></div>
                  <div><Label>Country</Label><Input value={form.billing_country} onChange={(e) => setForm({ ...form, billing_country: e.target.value })} /></div>
                  <div><Label>PIN Code</Label><Input value={form.billing_pin} onChange={(e) => setForm({ ...form, billing_pin: e.target.value })} data-testid="cust-bill-pin" /></div>
                </div>
              </section>

              {/* Shipping Address */}
              <section>
                <div className="flex items-center justify-between mb-2">
                  <div className="text-sm font-semibold text-slate-700">Shipping Address</div>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <Checkbox checked={form.same_as_billing} onCheckedChange={(v) => setForm({ ...form, same_as_billing: !!v })} data-testid="cust-same-addr" />
                    Same as billing address
                  </label>
                </div>
                {!form.same_as_billing && (
                  <div className="grid grid-cols-2 gap-3" data-testid="ship-addr-block">
                    <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.shipping_line1} onChange={(e) => setForm({ ...form, shipping_line1: e.target.value })} /></div>
                    <div className="col-span-2"><Label>Address Line 2</Label><Input value={form.shipping_line2} onChange={(e) => setForm({ ...form, shipping_line2: e.target.value })} /></div>
                    <div><Label>City</Label><Input value={form.shipping_city} onChange={(e) => setForm({ ...form, shipping_city: e.target.value })} /></div>
                    <div><Label>State</Label><Input value={form.shipping_state} onChange={(e) => setForm({ ...form, shipping_state: e.target.value })} /></div>
                    <div><Label>Country</Label><Input value={form.shipping_country} onChange={(e) => setForm({ ...form, shipping_country: e.target.value })} /></div>
                    <div><Label>PIN Code</Label><Input value={form.shipping_pin} onChange={(e) => setForm({ ...form, shipping_pin: e.target.value })} /></div>
                  </div>
                )}
              </section>

              {/* Legacy address fallback (collapsed) */}
              <section>
                <div className="text-xs uppercase tracking-[0.12em] text-slate-500 mb-1">Legacy Address (free text, optional)</div>
                <Textarea rows={2} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Leave empty — will auto-compose from billing fields above." />
              </section>
            </div>

            <SheetFooter className="mt-6 gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={save} disabled={busy} data-testid="save-customer-btn">Save</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by code, name, GSTIN, contact..." className="pl-9" data-testid="cust-search-input" />
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="customers-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">Code</th>
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">GSTIN</th>
              <th className="py-3 px-4">Contact</th>
              <th className="py-3 px-4">City</th>
              <th className="py-3 px-4">Credit Limit</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => (
              <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`cust-row-${c.code}`}>
                <td className="py-3 px-4 mono font-medium">{c.code}</td>
                <td className="py-3 px-4 text-slate-800">{c.name}</td>
                <td className="py-3 px-4 mono text-xs text-slate-600">{c.gstin || "—"}</td>
                <td className="py-3 px-4 text-slate-600 text-xs">
                  {c.contact || "—"}{c.phone ? ` · ${c.phone}` : ""}
                </td>
                <td className="py-3 px-4 text-slate-600 text-xs">{c.billing_city || "—"}</td>
                <td className="py-3 px-4 mono">{formatMoney(c.credit_limit, docCurrency(c.commercial))}</td>
                <td className="py-3 px-4 text-right">
                  <Button variant="ghost" size="icon" onClick={() => startEdit(c)} data-testid={`edit-cust-${c.code}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(c)} data-testid={`delete-cust-${c.code}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="py-10 text-center text-slate-500 text-sm">No customers match.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
